<script setup>
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import { $router } from '@/router'
import { $bus, mqtt } from '@/common/events'
import { $config, defaultServiceOptions } from '@/common/config'
import { nanoid } from '@/common/helper'
import { dialog } from '@/common/dialog'
import { hcu } from '@/common/hcu'
import { env } from '@/secrets/env'
import { useTitle } from '@/composables/title'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useCabinetState } from '@/composables/cabinet'
import { useTranslate } from '@/composables/language'
import { useSettings } from '@/composables/settings'
import { useSystemStatus } from '@/composables/system'
import { useSmartCard, onSmartCardReceived } from '@/composables/hardware/smartcard'
import { useAuthState } from '@/composables/auth'
import { canInterrupt } from '@/composables/interrupt'
import { useSessionTimer } from '@/composables/session'
import { CabinetMonitor } from '@/drivers/CabinetMonitor.js'
import { ConfigService } from '@/services/ConfigService.js'
import { AuthService } from '@/services/AuthService.js'
import ServiceNotice from '@/components/Maintenance/ServiceNotice.vue'
import DebugFooter from '@/components/Maintenance/DebugFooter.vue'

//const authService = new AuthService(defaultServiceOptions());
let authService = null;

const { t, translateError } = useTranslate();
const instance = getCurrentInstance();
const authStore = useAuthState();
const { smartcard } = useSmartCard();
const { latestStatus, isServiceMode } = useSystemStatus();
const { settings } = useSettings();
const { cabinetState } = useCabinetState();

const configService = new ConfigService();

const screen = ref("loading"); //loading, reference, error
const message = ref(null);
const retry = ref(true);
const deviceMac = ref(null);
const moving = ref(false);
const autoRef = ref(false);

const cabinets = ref(null);
const monitors = ref(null);

const isAnyDoorOpen = ref(false);
const hasAllDoorOpenStates = ref(false);
const masterCabinetId = ref();

let timer = null;
let firstSelfCheck = true;

onMounted(() => {
  useTitle(t('intro.title'));
  useMenu(false);
  useSubmenu(false);
  useTopBar(false);

  //Do not allow any external event interruptions (such as session timer expiration)
  canInterrupt(false);

  //Avoid session timeout while a tray is open
  useSessionTimer(false);

  //Only for design-time
  //screen.value = "done"; //loading, reference, pending, error, done

  authService = new AuthService(defaultServiceOptions(t));

  //Perform self-check
  selfCheck();
});

onUnmounted(() => {
  //Restore interruptions
  canInterrupt(true);

  //Detach events
  disposeCabinetMonitors();
});

//Set self-check timeout timer
function startTimer() {
  stopTimer();
  timer = setTimeout(() => {
    message.value = t('intro.timeout');
  }, 60 * 1000);
};

//Clear self-check timeout timer
function stopTimer() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
};

//Move to next screen
function next() {
  message.value = t('intro.redirecting');
  $router.push({ name: "Login" });
}

//Redirect to service window
function goToServiceWindow() {
  message.value = t('intro.redirecting');
  $router.push({ name: "ServiceWindow", query: { service: "true" } });
}

//Self-check hardware test
async function selfCheck() {
  try {
    /*
    //Check if wizard has been completed on first boot
    if (firstSelfCheck) {
      try {
        let flag = await configService.getFlag("wizard-completed");
        if (!flag || !flag.value) {
          console.log("[intro] Wizard not completed, redirecting to wizard");
          $router.push({ name: "Wizard" });
          return;
        }
      }
      catch (err) {
        //Flag not found means wizard was never completed
        console.log("[intro] Wizard flag not found, redirecting to wizard");
        $router.push({ name: "Wizard" });
        return;
      }
    }
    */

    if (firstSelfCheck) {
      console.log("[intro] Self-check started");
    }

    //Start local timeout timer
    startTimer();

    //Get list of cabinets from hcu (once)
    if (firstSelfCheck) {
      message.value = t('intro.checking_settings');
    }
    let result = await hcu.cabinet().getInstances2(); //This one is refreshing state
    //let result = cabinetState.value; //This one has static state

    //Check if any cabinet defined
    if (!result.controllers || !(result.controllers instanceof Array) || result.controllers.length == 0) {
      retry.value = false;
      stopTimer();
      next();
      return;
    }

    cabinets.value = result.controllers.filter(x => x.model != "none" && x.model != "combined");

    //Hack: redirect for testing
    if (cabinets.value.filter(x => x.model == "plt-m280t").length) {
      goToServiceWindow();
      return;
    }

    //Health check
    if (firstSelfCheck) {
      //console.log("[intro] Performing health check...");
      message.value = t('intro.checking_cabinets');

      //Watch status change
      initializeCabinetMonitors();

      //TODO: .filter(x => x.model != "plt-m280" && x.model != "plt-m280t") => x.hasDoorState

      //Any door open?
      isAnyDoorOpen.value = cabinets.value.filter(x => x.model != "plt-m100" && x.model != "plt-m280" && x.model != "plt-m280t").filter(x => x.isOpen === true).length > 0;
      hasAllDoorOpenStates.value = !(cabinets.value.filter(x => x.model != "plt-m100" && x.model != "plt-m280" && x.model != "plt-m280t").filter(x => x.isOpen !== true && x.isOpen !== false).length > 0);

      //Temporary work-around for PLT-L64
      if (cabinets.value.filter(x => x.model == "plt-l64").length > 0) {
        hasAllDoorOpenStates.value = true;
      }
    }

    firstSelfCheck = false;

    //Check if any controller is disconnected
    let disconnectedControllers = cabinets.value.filter(x => x.status != "open");
    if (disconnectedControllers.length > 0) {
      message.value = t('intro.components_not_responding');
      screen.value = "error";
      moving.value = false;
      stopTimer();
      //Retry self-check
      if (retry.value) {
        setTimeout(selfCheck, 3 * 1000);
      }
      return;
    }

    //Wait for all controllers to update states
    if (!hasAllDoorOpenStates.value) {
      message.value = t('intro.waiting_for_door');
      screen.value = "pending";
      moving.value = false;
      stopTimer();
      //Retry self-check
      if (retry.value) {
        setTimeout(selfCheck, 3 * 1000);
      }
      return;
    }

    //PLT-B414 requires referencing while PLT-Lxx locker does not
    let referencingControllers = cabinets.value.filter(x => (x.needsReferencing && !x.isReferenced) || x.isReferencing === true);
    if (referencingControllers.length > 0) {
      message.value = null;
      screen.value = "reference";
      stopTimer();
      if (autoRef.value) { //x.isReferencing == "false"
        await goToRef();
      }
      //Retry self-check
      if (retry.value) {
        setTimeout(selfCheck, 3 * 1000);
      }
      return;
    }

    //Stop local timeout timer
    stopTimer();

    //Check if any cabinet needs referencing
    if (screen.value == "reference") {
      screen.value = "done";
      instance?.proxy?.$forceUpdate();
      setTimeout(next, 5000);
    }
    else {
      next();
    }
  }
  catch (err) {
    console.error("[intro] Self-check error", err);
    err = translateError(err);

    //Stop local timeout timer
    stopTimer();

    //Display message
    message.value = err.message;
    if (err.isSyntaxError) {
      message.value = t('intro.hcu_not_responding');
    }

    //Keep reference stage active
    if (screen.value != "reference") {
      screen.value = "error";
    }
    
    //Retry self-check
    if (retry.value) {
      setTimeout(selfCheck, 10 * 1000);
    }
  }
}

//Start moving drum to reference position
async function goToRef() {
  try {
    //Prevent duplicate invocation
    if (moving.value) {
      return;
    }

    //Show loading animation
    moving.value = true;
    console.log("[intro] Go to ref")
    
    //Find cabinets that need referencing
    let referencingControllers = cabinets.value.filter(x => x.needsReferencing && !x.isReferenced);
    for (let cabinet of referencingControllers) {
      console.log("[intro] Moving to ref", cabinet.model, cabinet.mac);
      message.value = t('intro.moving_to_ref');

      //Async trigger - go to reference
      hcu.cabinet(cabinet.mac).goToRef2();
    }
  }
  catch (err) {
    //dialog(err); //Commented: if on secondary and primary not yet booted displays "Failed to fetch" but continues once the primary is ready; commented due to redundant message
    console.error("[intro] Go to ref", err);
  }
}

function initializeCabinetMonitors() {
  monitors.value = [ ];
  for (let cabinet of cabinets.value) {
    let monitor = new CabinetMonitor({ mqtt: mqtt, mac: cabinet.mac });
    monitor.on("status-change", (data) => {
      //Update status
      let cabinet = cabinets.value.find(x => x.mac == data.mac);
      if (cabinet) {
        cabinet.isReferencing = data.isReferencing;
        cabinet.isReferenced = data.isReferenced;
        cabinet.isOpen = data.isOpen;
        cabinet.status = data.status;

        const doorStateCabinets = cabinets.value.filter(x => x.model != "plt-m100" && x.model != "plt-m280" && x.model != "plt-m280t");
        let anyOpen = doorStateCabinets.filter(x => x.isOpen === true).length > 0;
        let anyUndefined = doorStateCabinets.filter(x => x.isOpen !== true && x.isOpen !== false).length > 0; //Skip PLT-L64
        isAnyDoorOpen.value = anyOpen;
        hasAllDoorOpenStates.value = !anyUndefined;

        //Temporary work-around for PLT-L64
        if (cabinets.value.filter(x => x.model == "plt-l64").length > 0) {
          hasAllDoorOpenStates.value = true;
        }

        instance?.proxy?.$forceUpdate();
      }
      
      //Commented: executes too frequently (many status change event triggered at once)
      //Check if all finished referencing
      // let anyLeftUnreferenced = cabinets.value.filter(x => x.needsReferencing && !x.isReferenced).length == 0;
      // if (!anyLeftUnreferenced) {
      //   stopTimer();
      //   selfCheck();
      // }
    });
    monitors.value.push(monitor);
  }
}

function disposeCabinetMonitors() {
  if (monitors.value && monitors.value.length) {
    for (let monitor of monitors.value) {
      monitor.dispose();
    }
    monitors.value = null;
  }
}

onSmartCardReceived(async (data) => {
  try {
    let code = data.code;
  
    //Must be of string type
    if (typeof code == "number") {
      code = code.toString();
    }

    //Special local RFID code to enter service window
    console.log(code, env.VITE_SERVICE_RFID_TOKEN);
    if (code == env.VITE_SERVICE_RFID_TOKEN) {
      goToServiceWindow();
      return
    }

    //Get user's permission without actually logging in - no session is being created on "preflight"
    let result = await authService.login({ rfid125: code, preflight: true });
    if (result.error) {
      throw new Error(result.error);
    }

    //Check if user is allowed to enter service window
    let permissions = result.user.permissions;
    let allowed = permissions.includes("cabinet.admin") || permissions.includes("cabinet.service");

    //Jump to service window if allowed
    if (allowed) {
      //Perform actual log in
      result = await authService.login({ rfid125: code });
      if (result.error) {
        throw new Error(result.error);
      }

      $config.sessionId = result.session.id;

      //Logged in
      authStore.context.session = result.session;
      authStore.context.user = result.user;
      authStore.context.jwt = result.jwt;
      authStore.context.permissions = result.user?.permissions || [];
      authStore.save();

      $bus.emit("login", { displayName: result.user?.displayName, username: result.user?.username });

      goToServiceWindow();
    }
  }
  catch (err) {
    console.error("[intro] onSmartCardReceived", err);
  }
});
</script>

<template>
  <div class="block w-full">
    <!-- Service mode notice -->
    <ServiceNotice :allow-skip="true" />

    <!-- Initial loading animation -->
    <div role="status" class="screen-container" v-if="screen == 'loading'">
      <div>
        <Spinner size="10" class="my-0 mx-auto" />
        <div class="mt-5" v-if="message">{{ message }}</div>
        <div class="mt-5" v-else>{{ $t("intro.loading") }}</div>
      </div>
    </div>

    <!-- Success message -->
    <div class="screen-container" v-if="screen == 'done'">
      <div class="text-center">
        <i class="fa fa-solid fa-circle-check text-green-500 huge-icon"></i>
        <div class="mt-10 text-lg font-bold">{{ $t('intro.done') }}</div>
        <div class="mt-5">{{ $t('intro.referenced') }}</div>
        <div class="mt-5" v-if="cabinets">
          <div v-for="cabinet of cabinets">
            <span class="message-green" v-if="!cabinet.needsReferencing || cabinet.isReferenced"><i class="fa fa-check"></i>{{ cabinet.mac }}</span>
            <span class="message-neutral" v-else><i class="fa fa-question"></i>{{ cabinet.mac }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Request to set reference position -->
    <div class="screen-container" v-if="screen == 'reference'">
      <div class="text-center">
        <i v-if="!isAnyDoorOpen" class="fa fa-arrows-spin huge-icon" :class="{ 'text-amber-400': !moving, 'text-green-500 animate-spin': moving }" style="animation-duration: 7s;"></i>
        <i v-else class="fa fa-door-open text-amber-400 huge-icon"></i>
        <div class="mt-10 text-lg font-bold">{{ $t('intro.interruption') }}</div>
        <div class="mt-5" v-if="isAnyDoorOpen">{{ $t('intro.close_doors') }}</div>
        <div class="mt-5" v-else>{{ $t('intro.click_ref_button') }}</div>
        <div class="mt-5">
          <Button type="primary" :enabled="!isAnyDoorOpen" v-if="!moving" @click="goToRef()">{{ $t('intro.start') }}</Button>
        </div>
        <div class="mt-5" v-if="cabinets && moving && !isAnyDoorOpen">
          <div v-for="cabinet of cabinets">
            <span class="message-green" v-if="!cabinet.needsReferencing || cabinet.isReferenced"><i class="fa fa-check"></i>{{ cabinet.mac }}</span>
            <span class="message-yellow relative" v-else-if="!cabinet.isReferenced || cabinet.status == 'error'"><i class="loader-ellipsis ml-3 mr-5 w-10 text-center"></i>{{ cabinet.mac }}</span>
            <span class="message-red" v-else-if="!cabinet.isReferenced || cabinet.status == 'error'"><i class="fa fa-xmark"></i>{{ cabinet.mac }}</span>
            <span class="message-neutral" v-else><i class="fa fa-question"></i>{{ cabinet.mac }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Pending message or technical error message -->
    <div class="screen-container" v-if="screen == 'error' || screen == 'pending'">
      <div class="text-center">
        <i class="fa fa-regular fa-clock text-amber-400 huge-icon" v-if="screen == 'pending'"></i>
        <i class="fa fa-exclamation-triangle text-amber-400 huge-icon" v-else></i>
        <div class="mt-5 text-lg font-bold" v-if="screen == 'pending'">{{ $t('intro.starting') }}</div>
        <div class="mt-5 text-lg font-bold" v-else>{{ $t('intro.tech_error') }}</div>
        <div class="mt-5" v-if="message">{{ message }}</div>
        <div class="mt-5" v-if="cabinets">
          <div v-for="cabinet of cabinets">
            <span class="message-green" v-if="cabinet.status == 'open'"><i class="fa fa-check"></i>{{ cabinet.mac }}</span>
            <span class="message-red" v-else-if="cabinet.status == 'closed' || cabinet.status == 'error'"><i class="fa fa-xmark"></i>{{ cabinet.mac }}</span>
            <span class="message-yellow" v-else-if="cabinet.status == 'init' || cabinet.status == 'connecting'"><i class="fa fa-ellipsis"></i>{{ cabinet.mac }}</span>
            <span class="message-neutral" v-else><i class="fa fa-question"></i>{{ cabinet.mac }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer-container" v-if="isServiceMode">
      <DebugFooter :show-ip="true" :show-time="true" />
    </div>

  </div>
</template>

<style scoped>
.screen-container {
  @apply grid min-h-screen place-items-center dark:bg-gray-800 dark:text-white;
}

.huge-icon {
  font-size: 210px;
}

.message-green {
  @apply text-green-700 font-mono dark:text-green-500;
}

.message-green i.fa {
  @apply text-green-500;
}

.message-yellow {
  @apply text-amber-600 font-mono dark:text-amber-500;
}

.message-yellow i.fa {
  @apply text-amber-500;
}

.message-red {
  @apply text-red-700 font-mono dark:text-red-500;
}

.message-red i.fa {
  @apply text-red-500;
}

.message-neutral {
  @apply text-gray-700 font-mono dark:text-gray-500;
}

.message-neutral i.fa {
  @apply text-gray-500;
}

.message-green i.fa,
.message-red i.fa,
.message-yellow i.fa,
.message-neutral i.fa {
  @apply mr-2 w-10 text-center;
}

.loader-ellipsis {
  display: inline-block;
  width: 15px;
  position: relative;
  top: -2px;
  aspect-ratio: 2;
  --_g: no-repeat radial-gradient(circle closest-side,#f59e0b 90%,#0000); /* text-amber-500: #f59e0b */
  background:
    var(--_g) 0%   50%,
    var(--_g) 50%  50%,
    var(--_g) 100% 50%;
  background-size: calc(100%/3) 50%;
  animation: l3 1s infinite linear;
}
@keyframes l3 {
    20%{background-position:0%   0%, 50%  50%,100%  50%}
    40%{background-position:0% 100%, 50%   0%,100%  50%}
    60%{background-position:0%  50%, 50% 100%,100%   0%}
    80%{background-position:0%  50%, 50%  50%,100% 100%}
}

.footer-container {
  @apply fixed flex flex-row justify-between left-0 bottom-0 w-full;
}
</style>
